import { useState } from "react";
import { toast } from "react-toastify";
import { Button, Input, Select, TextArea } from "../../../shared";
import { SettingsHeader } from "../index";
import { useConfig } from "../../../hooks";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/access-control";
import { isMotiveIntegrationEnabled } from "../../../utils/motiveIntegration";
import { useGetDriversQuery } from "../../../redux/services/drivers/driversApi";
import {
  useConnectMotiveMutation,
  useDisconnectMotiveMutation,
  useGetMotiveAssetsQuery,
  useGetMotiveStatusQuery,
  useGetMotiveUsersQuery,
  useGetMotiveVehiclesQuery,
  useImportMotiveAssetMutation,
  useLinkEldDriverMutation,
  useSyncMotiveFleetMutation,
} from "../../../redux/services/fleet/fleetApi";

/**
 * One row in a Motive catalog with an import action.
 * @param {{ title: string, rows: Array<{ id: string, label: string, detail?: string }>, empty: string, onImport: Function, importingId: string }} props
 */
function CatalogList({ title, rows, empty, onImport, importingId }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-800">{title}</p>
      {!rows.length ? (
        <p className="text-sm text-gray-600">{empty}</p>
      ) : (
        <ul className="divide-y rounded-lg border border-gray-200 bg-white">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <span>
                <span className="text-gray-900">{row.label}</span>
                {row.detail ? (
                  <span className="ml-2 text-gray-500">{row.detail}</span>
                ) : null}
              </span>
              <Button
                onClick={() => onImport(row.source)}
                loading={importingId === row.id}
              >
                Import
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Tenant Motive integration settings (opt-in telematics).
 */
const MotiveIntegrationsSettings = () => {
  const { configs } = useConfig();
  const motiveEnabled = isMotiveIntegrationEnabled(configs);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.INTEGRATIONS_MOTIVE_MANAGE);
  const { data, isLoading, refetch } = useGetMotiveStatusQuery(undefined, {
    skip: !motiveEnabled,
  });
  const [connectMotive, { isLoading: connecting }] = useConnectMotiveMutation();
  const [disconnectMotive, { isLoading: disconnecting }] =
    useDisconnectMotiveMutation();
  const [accessToken, setAccessToken] = useState("");
  const [driverId, setDriverId] = useState("");
  const [motiveUserId, setMotiveUserId] = useState("");
  const [importingId, setImportingId] = useState("");
  const [linkEldDriver, { isLoading: linking }] = useLinkEldDriverMutation();
  const [importMotiveAsset] = useImportMotiveAssetMutation();
  const [syncMotiveFleet, { isLoading: syncing }] = useSyncMotiveFleetMutation();

  const status = data?.data || data;
  const isConnected = status?.status === "connected" && status?.enabled;
  const listsReady = motiveEnabled && isConnected;

  const { data: driverData } = useGetDriversQuery(
    { page: 1, limit: 100 },
    { skip: !listsReady }
  );
  const { data: motiveUsersData, isLoading: usersLoading } = useGetMotiveUsersQuery(
    undefined,
    { skip: !listsReady }
  );
  const { data: vehiclesData, isLoading: vehiclesLoading } =
    useGetMotiveVehiclesQuery(undefined, { skip: !listsReady });
  const { data: assetsData, isLoading: assetsLoading } = useGetMotiveAssetsQuery(
    undefined,
    { skip: !listsReady }
  );

  const attuneDrivers = driverData?.drivers || [];
  const motiveUsers = motiveUsersData?.data?.users || [];
  const vehicles = vehiclesData?.data?.vehicles || [];
  const assets = assetsData?.data?.assets || [];

  async function handleConnect() {
    if (!accessToken.trim()) return;
    await connectMotive({ accessToken: accessToken.trim(), enabled: true }).unwrap();
    setAccessToken("");
    refetch();
  }

  async function handleDisconnect() {
    await disconnectMotive().unwrap();
    refetch();
  }

  async function handleLinkDriver() {
    if (!driverId || !motiveUserId) return;
    try {
      const result = await linkEldDriver({
        driverId,
        provider: "motive",
        externalUserId: motiveUserId,
      }).unwrap();
      if (result?.data?.motiveExternalIdSaved === false) {
        toast.warning(
          "Driver linked in Attune. Motive did not store the Attune driver id."
        );
      } else {
        toast.success("Driver linked");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Could not link that driver");
    }
  }

  async function handleImportVehicle(vehicle) {
    setImportingId(vehicle.id);
    try {
      await importMotiveAsset({
        externalAssetId: vehicle.id,
        externalVehicleId: vehicle.id,
        name: vehicle.number || `Truck ${vehicle.id}`,
        equipmentCategory: "truck",
        vin: vehicle.vin,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
      }).unwrap();
      toast.success(`Imported truck ${vehicle.number || vehicle.id}`);
    } catch (error) {
      toast.error(error?.data?.message || "Could not import that truck");
    } finally {
      setImportingId("");
    }
  }

  async function handleImportAsset(asset) {
    setImportingId(asset.id);
    try {
      await importMotiveAsset({
        externalAssetId: asset.id,
        name: asset.name || `Trailer ${asset.id}`,
        equipmentCategory: "trailer",
        vin: asset.vin,
      }).unwrap();
      toast.success(`Imported ${asset.name || "trailer"}`);
    } catch (error) {
      toast.error(error?.data?.message || "Could not import that trailer");
    } finally {
      setImportingId("");
    }
  }

  async function handleSyncFleet() {
    try {
      const result = await syncMotiveFleet().unwrap();
      const updated = result?.data?.updated ?? 0;
      toast.success(
        updated
          ? `Updated readings on ${updated} units`
          : "No linked trucks had a new reading"
      );
    } catch (error) {
      toast.error(error?.data?.message || "Could not update fleet readings");
    }
  }

  return (
    <div className="space-y-6">
      <SettingsHeader title="Integrations — Motive" />
      {!motiveEnabled ? (
        <p className="text-sm text-gray-600">
          Motive integration is not enabled for your organization yet.
        </p>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            Connect Motive only when you use their ELD/telematics. Fleet
            management works fully without this integration. The driver app
            clock stays time on duty for payroll. It is not an hours-of-service
            log. After a driver is linked, dispatch and the driver home read
            drive time left from Motive.
          </p>

          {isLoading ? (
            <p className="text-sm text-gray-500">Loading status…</p>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
              <div className="text-sm">
                <span className="font-medium text-gray-800">Status: </span>
                <span>{status?.status || "disconnected"}</span>
                {status?.enabled ? " (enabled)" : " (disabled)"}
              </div>
              {status?.lastSyncAt && (
                <div className="text-sm text-gray-600">
                  Last sync: {new Date(status.lastSyncAt).toLocaleString()}
                </div>
              )}
              {status?.lastError && (
                <TextArea
                  label="Last error"
                  value={status.lastError}
                  readOnly
                  rows={2}
                />
              )}
            </div>
          )}

          {canManage && !isConnected && (
            <div className="space-y-3 max-w-lg">
              <Input
                label="Motive API access token"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="Paste token from Motive developer settings"
                type="password"
              />
              <Button onClick={handleConnect} loading={connecting}>
                Connect Motive
              </Button>
            </div>
          )}

          {canManage && isConnected && (
            <Button type="danger" onClick={handleDisconnect} loading={disconnecting}>
              Disconnect Motive
            </Button>
          )}

          {canManage && isConnected && (
            <div className="space-y-3 max-w-lg">
              <p className="text-sm font-medium text-gray-800">Link a driver</p>
              <p className="text-sm text-gray-600">
                Match an Attune driver to their Motive user. Attune stores that
                Motive id, and writes the Attune driver id back onto the Motive
                user.
              </p>
              <Select
                label="Attune driver"
                value={driverId}
                onChange={(option) => setDriverId(option?.value || "")}
                options={attuneDrivers.map((driver) => ({
                  label:
                    [driver.firstName, driver.lastName].filter(Boolean).join(" ") ||
                    driver.fullName ||
                    driver.email ||
                    "Driver",
                  value: driver._id,
                }))}
                search
              />
              <Select
                label="Motive driver"
                value={motiveUserId}
                onChange={(option) => setMotiveUserId(option?.value || "")}
                options={motiveUsers.map((user) => ({
                  label:
                    [user.firstName, user.lastName].filter(Boolean).join(" ") ||
                    user.email ||
                    user.id,
                  value: user.id,
                }))}
                search
              />
              {usersLoading ? (
                <p className="text-sm text-gray-500">Loading Motive drivers…</p>
              ) : null}
              <Button onClick={handleLinkDriver} loading={linking}>
                Link driver
              </Button>
            </div>
          )}

          {canManage && isConnected && (
            <div className="space-y-4">
              {vehiclesLoading || assetsLoading ? (
                <p className="text-sm text-gray-500">Loading Motive equipment…</p>
              ) : null}
              <CatalogList
                title="Trucks"
                empty="No Motive trucks were returned."
                importingId={importingId}
                onImport={handleImportVehicle}
                rows={vehicles.map((vehicle) => ({
                  id: vehicle.id,
                  source: vehicle,
                  label: vehicle.number || vehicle.id,
                  detail: [vehicle.make, vehicle.model, vehicle.vin]
                    .filter(Boolean)
                    .join(" · "),
                }))}
              />
              <CatalogList
                title="Trailers"
                empty="No Motive trailers were returned."
                importingId={importingId}
                onImport={handleImportAsset}
                rows={assets.map((asset) => ({
                  id: asset.id,
                  source: asset,
                  label: asset.name || asset.id,
                  detail: [asset.type, asset.vin].filter(Boolean).join(" · "),
                }))}
              />
              <Button onClick={handleSyncFleet} loading={syncing}>
                Update fleet readings
              </Button>
              <p className="text-sm text-gray-600">
                Writes location, odometer, engine hours, and open fault count
                onto trucks already imported from Motive.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MotiveIntegrationsSettings;
